import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);

export const WishlistProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await api('/wishlist');
      setItems(data.items || data || []);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load wishlist on mount / auth change
  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const toggleWishlist = useCallback(async (productId) => {
    if (!isAuthenticated) return;

    const existing = items.find((item) => item.productId === productId);
    try {
      if (existing) {
        await api(`/wishlist/${productId}`, { method: 'DELETE' });
        setItems((prev) => prev.filter((item) => item.productId !== productId));
      } else {
        const data = await api(`/wishlist/${productId}`, { method: 'POST' });
        // API returns { item: {...} } — extract the item, not the wrapper
        setItems((prev) => [...prev, data.item || data]);
      }
    } catch (err) {
      console.error('Error toggling wishlist:', err);
      throw err;
    }
  }, [isAuthenticated, items]);

  const isInWishlist = useCallback(
    (productId) => items.some((item) => item.productId === productId),
    [items]
  );

  const value = useMemo(() => ({
    items,
    loading,
    toggleWishlist,
    isInWishlist,
    fetchWishlist,
  }), [items, loading, toggleWishlist, isInWishlist, fetchWishlist]);

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export default WishlistContext;
